import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";

import { QuoteRepository } from "../../quote.repository";
import { quoteLocationType } from "../quote-chips/quote-location-type.const";
import { Quote } from "../../quote.type";
import { QuoteLocation } from "../../models/quote-location.model";
import { NotifyService } from "../../../notify/notify.service";
import { TOAST } from "../../../notify/toast/toast-type.enum";
import { eventType } from "../../../notify/event.const";
import { BackendErrorPipe } from "../../../shared/backend/backend-error.pipe";
import { EventService } from "../../../shared/event/event.service";
import { PlaceDetails } from "app/shared/google-maps/place-autocomplete.service";
import { Bind } from "primeng/bind";
import { ButtonDirective } from "primeng/button";
import { AutoComplete } from "primeng/autocomplete";
import { FormsModule } from "@angular/forms";
import { Tooltip } from "primeng/tooltip";
import { InputText } from "primeng/inputtext";
import { CountrySelectComponent } from "../../../ui/dropdown/country-select/country-select.component";
import { PlaceAutocompleteComponent } from "../../../ui/dropdown/place-autocomplete/place-autocomplete.component";

@Component({
  selector: "app-quote-chips-single",
  templateUrl: "./quote-chips-single.component.html",
  styleUrls: ["../quote-chips/quote-chips.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    Bind,
    AutoComplete,
    FormsModule,
    Tooltip,
    InputText,
    CountrySelectComponent,
    PlaceAutocompleteComponent,
  ],
})
export class QuoteChipsSingleComponent implements OnInit {
  private _eref = inject(ElementRef);
  private quoteRepository = inject(QuoteRepository);
  private notifyService = inject(NotifyService);
  private eventService = inject(EventService);

  public types = quoteLocationType;

  public readonly type = input<string>(undefined);
  public readonly locationKindFilter = input("");
  public readonly locationKindFilterOptions = input([]);
  public readonly quote = input<Quote>(undefined);

  readonly locationChange = output<{
    type: string;
    locations: QuoteLocation[];
  }>();
  readonly locationKindFilterChange = output<string>();

  public addingLocation = false;

  // Set while the picked place's details are in flight, so the leg cannot be
  // saved before its coordinates and address have landed.
  public placePending = false;

  // Google Maps writes "00-000" when it returns no postal code for the place,
  // so the placeholder has to fail validation the same way an empty field does.
  public readonly postalCodePattern = "(?!00-000$).+";

  // TODO: This should be converted to Reactive Form, or QuoteLocation model
  private _locations = [];
  public currentEditedLocation: QuoteLocation = new QuoteLocation();
  private expanded = true;

  ngOnInit() {
    this.expanded = this.locations.length ? false : true;
  }

  @HostListener("document: mousedown", ["$event"]) onMouseDown(event) {
    if (
      !this._eref.nativeElement.contains(event.target) &&
      !this.hasSomeParentTheClass(event.target, "p-select-overlay") &&
      !this.hasSomeParentTheClass(event.target, "p-dropdown-item") &&
      !this.hasSomeParentTheClass(event.target, "p-dropdown-filter") &&
      !this.hasSomeParentTheClass(event.target, "p-autocomplete-list-item") &&
      !!this.locations &&
      this.locations.length
    ) {
      this.expanded = false;
      this.looseFocusOnEditedLocation();
    } else if (
      !this.expanded &&
      this._eref.nativeElement.contains(event.target)
    ) {
      this.expanded = true;
      this.emitLocationKindFromFirstLocationOnList();
    }
  }

  public onInlandHaulageChange(inlandHaulage: string): void {
    this.currentEditedLocation.inlandHaulage = inlandHaulage;
    this.clearDistance();
  }

  public onPlaceSelect(place: PlaceDetails): void {
    this.currentEditedLocation.inlandHaulage = place.label;
    this.currentEditedLocation.latitude = place.latitude;
    this.currentEditedLocation.longitude = place.longitude;
    this.currentEditedLocation.postalCode = place.postalCode ?? "00-000";
    this.currentEditedLocation.country = place.country ?? "";
  }

  @Input()
  set locations(locations: QuoteLocation[]) {
    if (locations !== null) {
      this._locations = locations;
    }
  }

  get locations(): QuoteLocation[] {
    return this._locations;
  }

  public isExpanded(): boolean {
    return this.expanded || !this.locations.length;
  }

  public editLocation(location: QuoteLocation): void {
    this.currentEditedLocation = location;
  }

  public looseFocusOnEditedLocation() {
    this.currentEditedLocation = new QuoteLocation();
  }

  public isValid() {
    return (
      this.currentEditedLocation.getPostalCode() &&
      this.currentEditedLocation.getPostalCode() !== "00-000" &&
      this.currentEditedLocation.getCountry()
    );
  }

  public removeElement(event) {
    const endpoint = this.isOrigin() ? "deleteOrigin" : "deleteDestination";

    this.emitQuoteUpdating();
    this.quoteRepository[endpoint](
      this.quote().getId(),
      event.value.getId(),
    ).subscribe({
      next: (response): void => {
        this.looseFocusOnEditedLocation();
        this.emitQuoteUpdated();
        this.notifyService.toast(TOAST.SUCCESS, "Location has been removed");
        this.locationChange.emit({
          type: this.type(),
          locations: this.locations,
        });
      },
      error: (err): void => {
        this.notifyService.toast(TOAST.ERROR, "Removing location error");
      },
    });
  }

  public clearDistance() {
    this.currentEditedLocation.latitude = null;
    this.currentEditedLocation.longitude = null;
  }

  private addElement() {
    const endpoint = this.isOrigin() ? "addOrigin" : "addDestination";

    this.addingLocation = true;
    this.emitQuoteUpdating();
    this.quoteRepository[endpoint](
      this.quote().getId(),
      null,
      this.currentEditedLocation.getInlandHaulage(),
      this.currentEditedLocation.getPostalCode(),
      this.currentEditedLocation.getLatitude(),
      this.currentEditedLocation.getLongitude(),
      this.currentEditedLocation.getCountry(),
    ).subscribe({
      next: (quoteLocation): void => {
        this.addingLocation = false;
        this.locations.push(quoteLocation);
        this.locations = [...this.locations];
        this.looseFocusOnEditedLocation();
        this.locationChange.emit({
          type: this.type(),
          locations: this.locations,
        });
        this.notifyService.toast(TOAST.SUCCESS, "Location has been added");
        this.emitQuoteUpdated();
      },
      error: (err): void => {
        this.addingLocation = false;
        this.notifyService.toast(
          TOAST.ERROR,
          "Adding location error",
          new BackendErrorPipe().transform(err, "string"),
        );
      },
    });
  }

  private saveInland() {
    const endpoint = this.isOrigin() ? "patchOrigin" : "patchDestination";

    this.emitQuoteUpdating();
    this.quoteRepository[endpoint](
      this.quote().getId(),
      this.currentEditedLocation.getId(),
      null,
      this.currentEditedLocation.getInlandHaulage(),
      this.currentEditedLocation.getPostalCode(),
      this.currentEditedLocation.getLatitude(),
      this.currentEditedLocation.getLongitude(),
      this.currentEditedLocation.getCountry(),
    ).subscribe({
      next: (quoteLocation): void => {
        this.emitQuoteUpdated();
        this.looseFocusOnEditedLocation();
        this.locationChange.emit({
          type: this.type(),
          locations: this.locations,
        });
        this.notifyService.toast(TOAST.SUCCESS, "Location has been added");
      },
      error: (err): void => {
        this.notifyService.toast(TOAST.ERROR, "Adding location error");
      },
    });
  }

  private hasSomeParentTheClass(element, classname) {
    if (
      typeof element.className === "string" &&
      element.className.split(" ").indexOf(classname) >= 0
    ) {
      return true;
    }

    return (
      element.parentNode &&
      this.hasSomeParentTheClass(element.parentNode, classname)
    );
  }

  private emitQuoteUpdating(): void {
    this.eventService.eventBus.emit({
      event: eventType.quoteUpdating,
      data: this.quote(),
    });
  }

  private emitQuoteUpdated(): void {
    this.eventService.eventBus.emit({
      event: eventType.quoteUpdated,
      data: this.quote(),
    });
  }

  private isOrigin(): boolean {
    return this.type() === quoteLocationType.origin;
  }

  private emitLocationKindFromFirstLocationOnList() {
    this.locationKindFilterChange.emit(
      this.locations && this.locations[0] && this.locations[0].getLocation()
        ? this.locations[0].getLocation().getKind()
        : "",
    );
  }
}
